export const TRANSLATION_SCHEMA_VERSION = 1;
export const MAX_CATALOG_BYTES = 2 * 1024 * 1024;
const REFRESH_INTERVAL = 5 * 60 * 1000;
const tokens = value => [...value.matchAll(/{{\s*(\w+)\s*}}/g)].map(match => match[1]).sort().join(',');
const isRecord = value => value && typeof value === 'object' && !Array.isArray(value);

// Validate against this client's bundled contract. Newer keys are ignored, so
// old clients can consume new copy while retaining their original placeholders.
export function validateTranslationBundle(bundle, baseline, language = 'en') {
    if (!isRecord(bundle) || bundle.schemaVersion !== TRANSLATION_SCHEMA_VERSION ||
        bundle.language !== language || !/^[a-f0-9]{64}$/.test(bundle.version || '') || !isRecord(bundle.catalogs)) {
        throw new Error('Invalid translation bundle');
    }
    const catalogs = {};
    for (const [namespace, entries] of Object.entries(baseline)) {
        const remote = bundle.catalogs[namespace];
        if (!isRecord(remote)) throw new Error('Missing translation namespace');
        catalogs[namespace] = {};
        for (const [key, english] of Object.entries(entries)) {
            const value = remote[key];
            if (typeof value !== 'string' || value.length > 10000 || tokens(value) !== tokens(english) ||
                value.includes('{{{') || value.includes('$t(')) {
                throw new Error('Incompatible translation value');
            }
            catalogs[namespace][key] = value;
        }
    }
    return {...bundle, catalogs};
}

export function createRemoteTranslations({i18n, baseline, endpoint, storage, fetcher, language = 'en', onApplied = () => {}, now = Date.now}) {
    const storageKey = `luckynote.translations.v1.${language}:${endpoint}`;
    let currentVersion = null;
    let restorePromise;
    let inFlight;
    let lastCheck = -Infinity;
    const apply = bundle => {
        // No awaits between namespace replacement and the notification: observers
        // see one complete version, never a partially applied catalog.
        for (const [namespace, entries] of Object.entries(bundle.catalogs)) {
            i18n.addResourceBundle(language, namespace, entries, true, true);
        }
        currentVersion = bundle.version;
        onApplied();
        i18n.emit('translationsUpdated');
    };
    const restore = () => {
        if (!restorePromise) restorePromise = (async () => {
            try {
                const saved = await storage.getItem(storageKey);
                if (saved && saved.length <= MAX_CATALOG_BYTES) apply(validateTranslationBundle(JSON.parse(saved), baseline, language));
            } catch (_) {
                // Corrupt, unavailable, or incompatible cache leaves bundled English intact.
            }
        })();
        return restorePromise;
    };
    const refresh = ({force = false} = {}) => {
        if (inFlight) return inFlight;
        inFlight = (async () => {
            await restore();
            if (!force && now() - lastCheck < REFRESH_INTERVAL) return {status: 'throttled'};
            lastCheck = now();
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 10000);
            try {
                const url = endpoint + (currentVersion ? `?version=${currentVersion}` : '');
                const response = await fetcher(url, {signal: controller.signal, cache: 'no-store', credentials: 'omit'});
                if (response.status === 304) return {status: 'unchanged'};
                if (!response.ok) throw new Error('Translation download failed');
                const content = await response.text();
                if (content.length > MAX_CATALOG_BYTES) throw new Error('Translation bundle too large');
                const bundle = validateTranslationBundle(JSON.parse(content), baseline, language);
                if (bundle.version === currentVersion) return {status: 'unchanged'};
                // Cache failures should not prevent a valid online copy update.
                try { await storage.setItem(storageKey, JSON.stringify(bundle)); } catch (_) {}
                apply(bundle);
                return {status: 'updated', version: currentVersion};
            } catch (_) {
                return {status: 'unavailable'};
            } finally {
                clearTimeout(timeout);
            }
        })().finally(() => { inFlight = null; });
        return inFlight;
    };
    return {restore, refresh};
}
